import { api } from "#/lib/api-client";
import type {
  AssetAvailabilityResponse,
  EscalateToProcurementRequest,
  FulfillAssetRequest,
  FulfillmentQueueItem,
  HrRequisition,
  HrRequisitionCreateRequest,
  HrRequisitionLine,
  HrRequisitionLineUpdateRequest,
  HrRequisitionStatus,
  HrRequisitionUpdateRequest,
  IssueStockRequest,
  PageResponse,
  RequisitionReportItem,
  RequisitionType,
  StockCheckResponse,
} from "./types";

const B = "/api/hr/requisitions";

/** Normalises Spring Boot 3.3+ nested-page responses.
 *  Old:  { content, totalElements, totalPages, number, size, first, last }
 *  New:  { content, page: { size, number, totalElements, totalPages } }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePage<T>(raw: any): PageResponse<T> {
  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body;
  return {
    content:       body?.content       ?? [],
    totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
    number:        meta?.number        ?? 0,
    size:          meta?.size          ?? 0,
    first:         meta?.number === 0,
    last:          (meta?.number ?? 0) + 1 >= (meta?.totalPages ?? meta?.total_pages ?? 1),
  };
}

function p(params: Record<string, unknown>) {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v).trim() !== "") out[k] = String(v);
  }
  return out;
}

export const hrRequisitionApi = {
  // ── Core CRUD ────────────────────────────────────────────────────────────────

  mine(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/mine`, {
      params: p({ page: params.page ?? 0, size: params.size ?? 10 }),
    }).then(r => normalizePage<HrRequisition>(r.data));
  },

  inbox(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/inbox`, {
      params: p({ page: params.page ?? 0, size: params.size ?? 10 }),
    }).then(r => normalizePage<HrRequisition>(r.data));
  },

  get(id: number) {
    return api.get<HrRequisition>(`${B}/${id}`).then(r => r.data);
  },

  create(payload: HrRequisitionCreateRequest) {
    return api.post<HrRequisition>(B, payload).then(r => r.data);
  },

  /** Only departmentId/requisitionType/purpose are accepted — there is no way to send
   *  lines through this endpoint. Use addLine/updateLine/deleteLine for line changes. */
  update(id: number, payload: HrRequisitionUpdateRequest) {
    return api.put<HrRequisition>(`${B}/${id}`, payload).then(r => r.data);
  },

  // ── Lines ─────────────────────────────────────────────────────────────────

  /** Classification fields (itemType/inventoryItemId/assetCategoryId) can only be set here,
   *  at creation — there is no way to reclassify an existing line afterwards. */
  addLine(id: number, line: Omit<HrRequisitionLine, "id">) {
    return api.post<HrRequisitionLine>(`${B}/${id}/lines`, line).then(r => r.data);
  },

  /** Only lineOrder/description/quantity are accepted — classification fields are ignored
   *  if sent here. To change a line's itemType/category, delete it and add it again. */
  updateLine(id: number, lineId: number, line: HrRequisitionLineUpdateRequest) {
    return api.put<HrRequisitionLine>(`${B}/${id}/lines/${lineId}`, line).then(r => r.data);
  },

  deleteLine(id: number, lineId: number) {
    return api.delete(`${B}/${id}/lines/${lineId}`);
  },

  // ── Workflow ──────────────────────────────────────────────────────────────

  submit(id: number) {
    return api.post<HrRequisition>(`${B}/${id}/submit`).then(r => r.data);
  },

  approve(id: number, comments?: string) {
    return api.post<HrRequisition>(`${B}/${id}/approve`, { comments }).then(r => r.data);
  },

  reject(id: number, comments?: string) {
    return api.post<HrRequisition>(`${B}/${id}/reject`, { comments }).then(r => r.data);
  },

  cancel(id: number, comments?: string) {
    return api.post<HrRequisition>(`${B}/${id}/cancel`, { comments }).then(r => r.data);
  },

  // ── Documents ────────────────────────────────────────────────────────────

  listDocuments(id: number) {
    return api.get(`${B}/${id}/documents`).then(r => r.data);
  },

  uploadDocument(id: number, file: File, documentType?: string) {
    const form = new FormData();
    form.append("file", file);
    if (documentType) form.append("documentType", documentType);
    return api.post(`${B}/${id}/documents`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },

  deleteDocument(id: number, documentId: number) {
    return api.delete(`${B}/${id}/documents/${documentId}`);
  },

  // ── V69 Fulfillment ──────────────────────────────────────────────────────

  /** Store Officer / Asset Manager inbox — approved requisitions awaiting action */
  pendingFulfillment(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/pending-fulfillment`, {
      params: p({ page: params.page ?? 0, size: params.size ?? 20 }),
    }).then(r => normalizePage<FulfillmentQueueItem>(r.data));
  },

  /** Per-line stock availability check before issuing */
  stockCheck(id: number) {
    return api.get<StockCheckResponse>(`${B}/${id}/stock-check`).then(r => r.data);
  },

  /** Issue consumable items from inventory stock against a GOODS requisition */
  issueStock(id: number, payload: IssueStockRequest) {
    return api.post<HrRequisition>(`${B}/${id}/issue-stock`, payload).then(r => r.data);
  },

  /** Assign specific assets from available pool against an ASSET_REQUEST requisition */
  fulfillAsset(id: number, payload: FulfillAssetRequest) {
    return api.post<HrRequisition>(`${B}/${id}/fulfill-asset`, payload).then(r => r.data);
  },

  /** Per-line availability *count* (requested vs available), NOT actual pickable Asset
   *  records — use assetApi.listAssets({ categoryId, status: "AVAILABLE" }) per line for
   *  the real picker list. */
  assetAvailability(id: number) {
    return api.get<AssetAvailabilityResponse>(`${B}/${id}/asset-availability`).then(r => r.data);
  },

  /** Store Officer escalates stock shortfall to procurement — creates a linked PurchaseRequisition */
  escalateToProcurement(id: number, payload: EscalateToProcurementRequest = {}) {
    return api.post(`${B}/${id}/escalate-to-procurement`, payload).then(r => r.data);
  },

  // ── Procurement workflow inboxes ─────────────────────────────────────────

  /** Finance Officer inbox — QUOTATIONS_RECEIVED requisitions awaiting budget decision */
  financeInbox(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/finance-inbox`, {
      params: p({ page: params.page ?? 0, size: params.size ?? 10 }),
    }).then(r => normalizePage<HrRequisition>(r.data));
  },

  /** MD inbox — EVALUATED requisitions awaiting final approval */
  mdInbox(params: { page?: number; size?: number } = {}) {
    return api.get(`${B}/md-inbox`, {
      params: p({ page: params.page ?? 0, size: params.size ?? 10 }),
    }).then(r => normalizePage<HrRequisition>(r.data));
  },

  // ── Procurement stage actions (external workflow) ─────────────────────────

  /** Procurement Officer: move SUBMITTED → QUOTATION_REQUESTED */
  requestQuotation(id: number, notes?: string) {
    return api.post<HrRequisition>(`${B}/${id}/request-quotation`, { notes }).then(r => r.data);
  },

  /** Procurement Officer: move QUOTATION_REQUESTED → QUOTATIONS_RECEIVED */
  quotationsReceived(id: number, notes?: string) {
    return api.post<HrRequisition>(`${B}/${id}/quotations-received`, { notes }).then(r => r.data);
  },

  /** Finance Officer: move QUOTATIONS_RECEIVED → BUDGET_APPROVED */
  budgetApprove(id: number, notes?: string) {
    return api.post<HrRequisition>(`${B}/${id}/budget-approve`, { notes }).then(r => r.data);
  },

  /** Finance Officer: move QUOTATIONS_RECEIVED → BUDGET_REJECTED */
  budgetReject(id: number, notes?: string) {
    return api.post<HrRequisition>(`${B}/${id}/budget-reject`, { notes }).then(r => r.data);
  },

  /** Procurement Officer: move BUDGET_APPROVED → EVALUATED */
  evaluate(id: number, notes?: string) {
    return api.post<HrRequisition>(`${B}/${id}/evaluate`, { notes }).then(r => r.data);
  },

  /** MD: move EVALUATED → APPROVED */
  mdApprove(id: number, notes?: string) {
    return api.post<HrRequisition>(`${B}/${id}/md-approve`, { notes }).then(r => r.data);
  },

  /** MD: move EVALUATED → REJECTED */
  mdReject(id: number, notes?: string) {
    return api.post<HrRequisition>(`${B}/${id}/md-reject`, { notes }).then(r => r.data);
  },

  // ── V69 Reports ──────────────────────────────────────────────────────────

  /** Paginated requisition report (manager view) */
  report(params: { page?: number; size?: number; status?: HrRequisitionStatus; type?: RequisitionType } = {}) {
    return api.get(`${B}/report`, {
      params: p({ page: params.page ?? 0, size: params.size ?? 20, status: params.status, type: params.type }),
    }).then(r => normalizePage<RequisitionReportItem>(r.data));
  },

  /** Download full requisition register as Excel */
  reportExport(params: { status?: HrRequisitionStatus; type?: RequisitionType } = {}) {
    return api.get<Blob>(`${B}/report/export`, {
      responseType: "blob",
      params: p({ status: params.status, type: params.type }),
    }).then(r => r.data);
  },
};

/**
 * Self lookup — same GET /api/hr/employees/me endpoint already used by Leave/Claims — used
 * here to prefill the create form's department with the requester's own, since departmentId
 * is @NotNull with no server-side fallback (confirmed 2026-07-08).
 */
export function fetchCurrentEmployee() {
  return api
    .get<{ id: number; primaryDepartmentId?: number | null; departmentName?: string | null }>(
      "/api/hr/employees/me",
    )
    .then(r => r.data);
}
